import { clsx } from 'clsx';
import { TableCell, TableRow } from 'src/components/ui/table';
import { getKnowledgeBaseIcon } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseIcons/CurrentKnowledgeBaseIcons';
import { AiKnowledgeBaseToolTip } from 'src/pages/AIKnowledgeBasePage/AiKnowledgeBaseToolTip';
import { KnowledgeBaseAutosyncIcon } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseIcons/CurrentKnowledgeBaseIcons';
import React, { useEffect } from 'react';
import { getCheckedFiles, setCheckedChildren } from 'src/utils/fileUtils';
import { ActionCellFiles } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseTableList/ActionCellFiles';
import { ActionCellIntegration } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseTableList/ActionCellIntegration';
import { CurrentKnowledgeBaseTableNameCell } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseTableList/CurrentKnowledgeBaseTableRow/CurrentKnowledgeBaseTableNameCell/CurrentKnowledgeBaseTableNameCell';
import { useKBContext } from 'src/provider/KnowledgeBaseProvider';
import { useAppSelector } from 'src/store';
import { FILE_STATE } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/CurrentKnowledgeBaseConstants';

export const CurrentKnowledgeBaseTableRow = ({
  item,
  handleRowClick,
  handleSingleItemEdit,
  handleSingleItemMove,
  handleSingleItemDelete,
  onCheckedChange,
  deleteContentMode,
  moveContentMode,
}) => {
  const { isTrialPlan } = useAppSelector((s) => s.planSlice);
  const is = {
    file: item.type === 'file',
    folder: item.type === 'folder',
    faq: item.type === 'FAQ',
    md: item.name.endsWith('.md'),
    txt: item.name.endsWith('.txt'),
    integration: item.type === 'integration',
    collector: item.type === 'integration' && item.data && !item.data.collector_id,
    delete: deleteContentMode,
    move: moveContentMode,
    deleting: item.state === FILE_STATE.DELETING,
    uploading: item.state === FILE_STATE.UPLOADING,
    error: [FILE_STATE.UPLOAD_ERROR, FILE_STATE.DELETE_ERROR].includes(item.state),
    website: item.type === 'website',
    zendeskTickets: item.data && item.data.type === 'zendesk_tickets',
    freshdeskTickets: item.data && item.data.type === 'freshdesk_tickets',
    trial: isTrialPlan,
  };
  const [checked, setChecked] = React.useState(item.checked);
  const [checkedCount, setCheckedCount] = React.useState(0);
  const { activeBranch } = useKBContext();

  const onChange = (checked, item) => {
    item.checked = checked;
    setChecked(checked);

    if (is.folder) {
      setCheckedChildren(item, checked);
    }
    onCheckedChange();
  };

  useEffect(() => {
    setChecked(item.checked);
  }, [activeBranch]);

  useEffect(() => {
    if (checked && is.folder) {
      const checkedFiles = getCheckedFiles(item).filter((file) => file.type !== 'folder');
      setCheckedCount(checkedFiles.length);
    } else {
      setCheckedCount(0);
    }
  }, [checked]);

  const onClick = (item) => {
    if ((is.delete || is.move) && is.file) {
      item.checked = !checked;
      setChecked(!checked);
      onCheckedChange();
    }
    handleRowClick(item);
  };

  return item.visible ? (
    <TableRow
      onClick={() => onClick(item)}
      className={clsx(
        'transition cursor-pointer hover:shadow-[0px_1px_2px_0px_rgba(0,_0,_0,_0.25)]',
        (is.deleting || is.uploading) && 'opacity-50 pointer-events-none',
        !checked && is.folder && 'bg-[#F1F6F6] hover:bg-[#E2EAE8]',
        (is.website || is.integration) && !item.collector_id && 'bg-[#F1F6F6] hover:bg-[#E2EAE8]',
        (is.website || is.integration) && item.collector_id && 'hover:bg-[#F8FBFA]',
        is.error && 'bg-[#FF51511A] hover:bg-[#FF515133]',
        !is.error && !checked && is.file && 'hover:bg-[#F8FBFA]',
        !is.error && !checked && is.faq && 'hover:bg-[#F8FBFA]',
        checked && is.delete && is.folder && 'bg-[#AA1E1E26] hover:bg-[#AA1E1E40]',
        checked && is.delete && is.file && 'bg-[#FF51511A] hover:bg-[#FF515133]',
        checked && is.delete && is.faq && 'bg-[#FF51511A] hover:bg-[#FF515133]',
        checked && is.move && is.folder && 'bg-[#DBF0E0] hover:bg-[#CAE7D1]',
        checked && is.move && is.file && 'bg-[#F4FFF7] hover:bg-[#E8FCED]',
        checked && is.move && is.faq && 'bg-[#F4FFF7] hover:bg-[#E8FCED]',
      )}
    >
      <TableCell
        padding={'pl-5 pr-0'}
        className="table-fixedt text-center min-w-[50px] max-w-[100px] !border-l-0 !rounded-none"
      >
        <div className="whitespace-nowrap hyphens-auto truncate flex items-center justify-center gap-2.5">
          <div>{getKnowledgeBaseIcon(item)}</div>
          <div
            className={clsx(
              'min-w-6 bg-white text-tiny font-semibold py-1 px-1',
              is.folder && checked ? 'visible' : 'invisible',
            )}
          >
            {checkedCount}
          </div>
        </div>
      </TableCell>
      <CurrentKnowledgeBaseTableNameCell item={item} is={is} />
      <TableCell padding={'p-5'} className="table-fixed text-center max-w-80">
        <p
          title={item.type}
          className={clsx(
            'font-medium whitespace-nowrap  hyphens-auto text-sm truncate capitalize',
            is.error ? 'text-[#FF515180]' : 'text-ebony_opacity_50',
          )}
        >
          {item.type}
        </p>
      </TableCell>
      <TableCell padding={'p-5'} className="table-fixed text-center max-w-80">
        <p
          title={item.localDate}
          className={clsx(
            'font-medium whitespace-nowrap hyphens-auto text-sm truncate capitalize',
            is.error ? 'text-[#FF515180]' : 'text-ebony_opacity_50',
          )}
        >
          {item.localDate}
        </p>
      </TableCell>
      {!is.trial && (
        <TableCell padding={'p-5'} className="table-fixed text-center max-w-80">
          <AiKnowledgeBaseToolTip
            side={'top'}
            trigger={
              <div className={'flex items-center justify-center'}>
                <KnowledgeBaseAutosyncIcon color={item.autosync.color} />
              </div>
            }
            content={
              <span className={'text-xs text-ebony_opacity_50'}>{item.autosync.status}</span>
            }
          />
        </TableCell>
      )}
      {Boolean(is.file || is.folder || is.faq) && (
        <ActionCellFiles
          item={item}
          handleSingleItemMove={handleSingleItemMove}
          handleSingleItemEdit={handleSingleItemEdit}
          handleSingleItemDelete={handleSingleItemDelete}
          is={is}
          checked={checked}
          onChange={onChange}
        />
      )}
      {(is.integration || is.website) && (
        <ActionCellIntegration
          item={item}
          handleSingleItemMove={handleSingleItemMove}
          handleSingleItemEdit={handleSingleItemEdit}
          handleSingleItemDelete={handleSingleItemDelete}
          is={is}
        />
      )}
    </TableRow>
  ) : null;
};
